import { create } from "zustand"

export type UnsentChannel = "email" | "slack" | "live-chat"

export interface UnsentMessage {
	id: string
	threadId: string
	channel: UnsentChannel
	html: string
	createdAt: number
	status: "sending" | "failed"
	serverId?: string
}

interface UnsentMessagesState {
	messages: Record<string, UnsentMessage>
	retries: Record<string, () => void>
	queued: Record<string, true>
	track: (message: Omit<UnsentMessage, "status" | "createdAt">) => void
	fail: (id: string, retry: () => void) => void
	settle: (id: string) => void
	confirm: (id: string, serverId: string) => void
	queue: (id: string) => void
	settleQueued: () => void
}

export const useUnsentMessages = create<UnsentMessagesState>((set) => ({
	messages: {},
	retries: {},
	queued: {},
	track: (message) =>
		set((state) => ({
			messages: {
				...state.messages,
				[message.id]: { ...message, status: "sending", createdAt: Date.now() },
			},
		})),
	fail: (id, retry) =>
		set((state) => {
			const message = state.messages[id]
			if (!message) return state
			return {
				messages: { ...state.messages, [id]: { ...message, status: "failed" } },
				retries: { ...state.retries, [id]: retry },
			}
		}),
	settle: (id) =>
		set((state) => {
			if (!state.messages[id]) return state
			const { [id]: _message, ...messages } = state.messages
			const { [id]: _retry, ...retries } = state.retries
			const { [id]: _queued, ...queued } = state.queued
			return { messages, retries, queued }
		}),
	confirm: (id, serverId) =>
		set((state) => {
			const message = state.messages[id]
			if (!message) return state
			return {
				messages: { ...state.messages, [id]: { ...message, serverId } },
			}
		}),
	queue: (id) =>
		set((state) =>
			state.messages[id] ? { queued: { ...state.queued, [id]: true } } : state,
		),
	settleQueued: () =>
		set((state) => {
			const messages = { ...state.messages }
			for (const id of Object.keys(state.queued)) delete messages[id]
			return { messages, queued: {} }
		}),
}))

/**
 * Replies the timeline has no row for: live chat before the server echoes it,
 * or a Zero write that was rolled back after it failed.
 */
export function visibleUnsent(
	messages: UnsentMessage[],
	threadId: string,
	renderedIds: ReadonlySet<string>,
): UnsentMessage[] {
	return messages
		.filter(
			(message) =>
				message.threadId === threadId &&
				!renderedIds.has(message.id) &&
				!(message.serverId && renderedIds.has(message.serverId)) &&
				// An email's own row is written optimistically a frame later; the raw
				// composer HTML would flash in uncollapsed, quoted history and all.
				!(message.channel === "email" && message.status === "sending"),
		)
		.sort((a, b) => a.createdAt - b.createdAt)
}

/** Ids of the reply rows a timeline already shows. */
export function messageIds(
	events: ReadonlyArray<{
		type: string
		email?: { id: string }
		slackMessage?: { id: string }
		liveChatMessage?: { id: string }
	}>,
): ReadonlySet<string> {
	const ids = new Set<string>()
	for (const event of events) {
		const id =
			event.email?.id ?? event.slackMessage?.id ?? event.liveChatMessage?.id
		if (id) ids.add(id)
	}
	return ids
}
